import type { FC } from "react"

import { useFormatDateRange } from "@/formatters"
import type { DateRangeBound } from "@/formatters"
import type { IDateFormat } from "@/models/date/date"

interface IDateRangeDisplayProps {
  /**
   * The end date
   */
  end: DateRangeBound

  /**
   * The format configuration for the date range
   */
  format?: IDateFormat

  /**
   * Whether a missing range renders "--". Defaults to true.
   */
  showPlaceholder?: boolean

  /**
   * The start date
   */
  start: DateRangeBound

  /**
   * Zone to display. Omitted, a DateTime keeps its own zone.
   */
  timeZone?: string
}

/**
 * A component that properly formats and internationalizes the provided date range.
 * Renders the string from useFormatDateRange. A bound is an ISO string or a DateTime.
 *
 * @example
 * <DateRangeDisplay start="2021-01-01" end="2021-02-28" format={{
 *   year: "numeric"
 * }} />
 */
export const DateRangeDisplay: FC<IDateRangeDisplayProps> = ({
  end,
  format,
  showPlaceholder,
  start,
  timeZone,
}) => {
  const { formatDateRange } = useFormatDateRange(format)
  const text = formatDateRange(start, end, { showPlaceholder, timeZone })

  if (text === "") {
    return null
  }

  return <span>{text}</span>
}
